/**
 * System Health presentation helpers — PURE, unit-testable. Map a check status
 * to its icon/label/colour, and roll the 8 checks up into the headline strip.
 * The server owns each status; this only drives how it renders.
 */
import type { HealthStatus } from '@/lib/schemas/health'

export interface StatusDisplay {
  /** Glyph shown in the card badge. */
  icon: string
  /** Screen-reader / tooltip label. */
  label: string
  /** Tailwind text/border/bg classes for the badge chip. */
  className: string
}

const STATUS: Record<HealthStatus, StatusDisplay> = {
  ok: {
    icon: '✓',
    label: 'OK',
    className: 'border-green-300 bg-green-50 text-green-800',
  },
  warn: {
    icon: '⚠',
    label: 'Warning',
    className: 'border-amber-300 bg-amber-50 text-amber-800',
  },
  error: {
    icon: '✕',
    label: 'Error',
    className: 'border-red-300 bg-red-50 text-red-800',
  },
  info: {
    icon: 'i',
    label: 'Info',
    className: 'border-blue-300 bg-blue-50 text-blue-800',
  },
}

export function statusDisplay(status: HealthStatus): StatusDisplay {
  return STATUS[status]
}

export interface HealthHeadline {
  /** True when no check is warn/error (all ok/info). */
  allNormal: boolean
  warnings: number
  errors: number
  text: string
}

/**
 * Roll the checks into the headline. All ok/info → "All systems normal";
 * otherwise "N warnings / N errors — check the audit log before calling Anthony."
 * (only the non-zero counts are listed).
 */
export function healthHeadline(checks: Array<{ status: HealthStatus }>): HealthHeadline {
  const warnings = checks.filter((c) => c.status === 'warn').length
  const errors = checks.filter((c) => c.status === 'error').length
  const allNormal = warnings === 0 && errors === 0

  if (allNormal) {
    return { allNormal, warnings, errors, text: 'All systems normal' }
  }

  const parts: string[] = []
  if (warnings > 0) parts.push(`${warnings} warning${warnings === 1 ? '' : 's'}`)
  if (errors > 0) parts.push(`${errors} error${errors === 1 ? '' : 's'}`)
  return {
    allNormal,
    warnings,
    errors,
    text: `${parts.join(' / ')} — check the audit log before calling Anthony.`,
  }
}
